import React, {useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {Controller} from 'react-hook-form';
import get from 'lodash/get';
import {makeStyles} from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import InputAdornment from '@material-ui/core/InputAdornment';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import LabelValue from '../../../shared/labelvalue/LabelValue.js';
import GenericTab from '../../../shared/generictab/GenericTab.js';
import {tituloSecao} from '../../../../utils/styles.js';
import {indiceSecaoVisivel} from '../../components/inscricao/inscricaoAdministrativaSchema.js';

/* Mesmas larguras e títulos das demais etapas da inscrição pública. */
const useStyles = makeStyles(theme => ({
    tabs: {
        width: '100%',
        minWidth: '180px',
        maxWidth: '220px',
        marginRight: theme.spacing(2),
        borderRight: `1px solid ${theme.palette.divider}`,
        '& .MuiTab-root': {
            '&.MuiButtonBase-root.MuiTab-root.MuiTab-textColorPrimary': {
                width: '100%',
                minWidth: '180px',
                maxWidth: '220px',
                alignItems: 'flex-start',
                textAlign: 'left'
            }
        }
    },
    sectionTitle: {
        ...tituloSecao(theme),
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2)
    }
}));

/*
 * Etapa "Análise" (só do administrativo): tipo e percentual de bolsa,
 * observação e dívida (veterano); atendente.
 * O tipo de bolsa já gravado que não está na lista (sigla nova do SAF) entra
 * como opção para não se perder ao salvar, como no PrimeFaces.
 */
export default function EtapaAnalise(props) {
    const {
        control,
        errors,
        watch,
        setValue,
        secoesComErro,
        secaoFoco,
        tiposBolsa = [],
        exibirDivida,
        atendentes = []
    } = props;
    const classes = useStyles();
    const tabRef = useRef();

    const tipoAtual = watch('tipoBolsa');
    const opcoesBolsa = tipoAtual && !tiposBolsa.includes(String(tipoAtual).trim())
        ? [...tiposBolsa, String(tipoAtual).trim()]
        : tiposBolsa;

    const erro = nome => get(errors, nome);

    const titulo = texto => (
        <Grid item xs={12}>
            <h4 className={classes.sectionTitle}>{texto}</h4>
        </Grid>
    );

    const campoTexto = (nome, rotulo, {multiline = false, sm = 6, adornment, type} = {}) => (
        <Grid item xs={12} sm={sm}>
            <LabelValue label={rotulo}>
                <FormControl variant='outlined' fullWidth error={!!erro(nome)}>
                    <Controller
                        name={nome}
                        control={control}
                        render={({field}) => (
                            <OutlinedInput
                                name={field.name}
                                value={field.value ?? ''}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                inputRef={field.ref}
                                type={type}
                                multiline={multiline}
                                minRows={multiline ? 5 : undefined}
                                endAdornment={adornment}
                                inputProps={type === 'number' ? {min: 0, max: 100, step: 1} : undefined}/>
                        )}/>
                    {!!erro(nome) && <FormHelperText error>{erro(nome).message}</FormHelperText>}
                </FormControl>
            </LabelValue>
        </Grid>
    );

    const campoSelect = (nome, rotulo, opcoes) => (
        <Grid item xs={12} sm={6}>
            <LabelValue label={rotulo}>
                <FormControl variant='outlined' fullWidth>
                    <Controller
                        name={nome}
                        control={control}
                        render={({field}) => (
                            <Select {...field} value={field.value ?? ''} displayEmpty>
                                <MenuItem value=''><em>Selecione...</em></MenuItem>
                                {opcoes.map(opcao => (
                                    <MenuItem key={opcao.valor} value={opcao.valor}>{opcao.descricao}</MenuItem>
                                ))}
                            </Select>
                        )}/>
                </FormControl>
            </LabelValue>
        </Grid>
    );

    const secoes = [
        {
            id: 'analise',
            label: 'Análise',
            content: (
                <Grid container spacing={3}>
                    {titulo('Análise')}
                    {campoSelect('tipoBolsa', 'Tipo de bolsa', opcoesBolsa.map(tipo => ({valor: tipo, descricao: tipo})))}
                    {campoTexto('percentualBolsa', 'Percentual de bolsa', {type: 'number', adornment: <InputAdornment position='end'>%</InputAdornment>})}
                    {campoTexto('observacao', 'Observação', {multiline: true, sm: 12})}
                    {exibirDivida && campoTexto('inscricaoVeterano.divida', 'Dívida', {multiline: true, sm: 12})}
                </Grid>
            )
        },
        {
            id: 'atendente',
            label: 'Atendente',
            content: (
                <Grid container spacing={3}>
                    {titulo('Atendente')}
                    {campoSelect('idAtendente', 'Atendente', atendentes.map(atendente => ({valor: String(atendente.id), descricao: atendente.nome})))}
                </Grid>
            )
        }
    ].map(secao => ({...secao, hasError: secoesComErro.has(secao.id)}));

    useEffect(() => {
        if (!secaoFoco) return;
        const indice = indiceSecaoVisivel(secoes, secaoFoco.secao);
        if (indice >= 0 && tabRef.current) {
            tabRef.current.setTabIndex(indice);
        }
    }, [secaoFoco]);

    return (
        <GenericTab
            ref={tabRef}
            orientation='vertical'
            noAppBar
            indicatorColor='primary'
            textColor='primary'
            scrollButtons='auto'
            className={classes.tabs}
            content={secoes}/>
    );
}

EtapaAnalise.propTypes = {
    control: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    watch: PropTypes.func.isRequired,
    setValue: PropTypes.func.isRequired,
    secoesComErro: PropTypes.instanceOf(Set).isRequired,
    secaoFoco: PropTypes.object,
    tiposBolsa: PropTypes.array,
    exibirDivida: PropTypes.bool,
    atendentes: PropTypes.array
};
